'use client';

// imports
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import { continueWatching } from '@/lib/resources/continue-watching';
import Link from "next/link";
import { useAtom } from 'jotai';
import { useState, useEffect } from 'react';

// lib files
import { canAccessStorage } from "@/lib/helpers/is-storage-available";
import { ContinueWatchingData } from '@/lib/types/api/home-data';
import { EXPLORE_TOPIC_CONFIGS } from '@/lib/constants';
import { formatVideoMetadata } from "@/lib/helpers/format-video-metadata";
import { INTERNALLY_NAVIGATING_KEY } from '@/lib/constants';
import { ProfileData } from '@/lib/types/api/profile-data';
import { ShowDetails, FeaturedPreview } from "@/lib/types/api/show-data";
import { StationData } from '@/lib/types/api/stations-data';
import { useIsPreviewVideoPlayingWebKit } from '@/lib/hooks';
import { useIdentity } from '@/lib/hooks/useIdentity';
import { VideoClass, VideoTypeEnum, EpisodeParent } from "@/lib/types/api/video";

// components
import BackButton from "@/components/Button/BackButton";
import ExpandableText from "@/components/ExpandableText/ExpandableText";
import FromFranchise from '@/components/FromFranchise/FromFranchise';
import ITSImage from "@/components/ITSImage/ITSImage";
import KabobMenu from "@/components/KabobMenu/KabobMenu";
import LinkButton from "@/components/Button/LinkButton";
import MezzanineVideoInitializer from '@/components/MezzanineVideoInitializer/MezzanineVideoInitializer';
import MyListButton from '@/components/Button/MyList/MyListButton';
import ShowDetailNoAssetsMessage from '@/components/ShowDetailHero/ShowDetailNoAssetsMessage';
import SponsorshipVideoHelpRow from '@/components/SponsorshipVideoHelpRow/SponsorshipVideoHelpRow';
import UserProgressBar from "@/components/UserProgressBar/UserProgressBar";
import VideoPlayer from "@/components/VideoPlayer/VideoPlayer";

// svgs
import ExternalLinkIcon from '@/public/svg/external-link.svg';

// styles
import styles from './ShowDetailHero.module.scss';

interface ShowDetailHeroProps {
  videoToDirectTo?: VideoClass;
  depContinueWatchingData?: ContinueWatchingData["content"];
  depIsLoggedIn?: boolean;
  depPercentageWatched?: number;
  episodeToDirectTo?: VideoClass;
  featuredPreview?: FeaturedPreview;
  host: string;
  isSVP: boolean;
  profileStub?: ProfileData;
  show: ShowDetails;
  sponsorInfoLink?: string;
  stationData?: StationData;
  hasFullLengthAssets?: boolean;
}

const ShowDetailHero: React.FC<ShowDetailHeroProps> = (props) => {
  const {
    videoToDirectTo,
    depContinueWatchingData,
    depIsLoggedIn,
    depPercentageWatched,
    featuredPreview,
    host,
    isSVP,
    show,
    sponsorInfoLink,
    stationData,
    hasFullLengthAssets,
  } = props;

  // to adjust show detail page elements for improved closed captions visibility on Safari at mobile breakpoints
  const isPreviewVideoPlayingWebKit: boolean = useIsPreviewVideoPlayingWebKit();

  const [showPreviewFromButton, setShowPreviewFromButton] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [isInternallyNavigating, setIsInternallyNavigating] = useState(false);
  const [continueWatchingData] = useAtom(continueWatching.itemsAtom);
  const { isAuthenticated } = useIdentity()
  const [hasPlayed, setHasPlayed] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Client-safe user state - prevents hydration mismatch
  const isLoggedIn = depIsLoggedIn === true || (isClient && isAuthenticated);
  const continueWatchingDataToUse = depContinueWatchingData || (isClient ? continueWatchingData : null);
  const continueWatchingVideo = continueWatchingDataToUse?.find((video) => {
    return video.show.slug === show.slug;
  });
  const isShowInContinueWatchingData = continueWatchingVideo !== undefined;
  const getMinutesLeft = (duration: number, secondsWatched: number) => {
    const secondsLeft = duration - secondsWatched;
    return Math.floor(secondsLeft / 60);
  }

  let minLeft = 0;

  if (continueWatchingVideo) {
    minLeft = getMinutesLeft(continueWatchingVideo.duration, continueWatchingVideo.seconds_watched);
  }

  // Get the metadata for the continue watching video
  const continueWatchingVideoMetadata = formatVideoMetadata({
    show: {
      season: (continueWatchingVideo?.parent as EpisodeParent)?.season?.ordinal,
      display_episode_number: true,
      seasons_count: show.seasons_count,
      episode: (continueWatchingVideo?.parent as EpisodeParent)?.ordinal,
    },
    video_type: continueWatchingVideo?.video_type || 'episode',
    parent_type: continueWatchingVideo?.parent.resource_type || 'episode',
    duration: continueWatchingVideo?.duration || 0,
  }, "shortEpOnly");
  // and construct a summary to be used to provide more context to the user of which video they're
  // being suggested to continue watching
  const continueWatchingVideoSummary = `${continueWatchingVideoMetadata ? `${continueWatchingVideoMetadata}:` : ""} ${continueWatchingVideo?.title}`;

  // We show a continue watching hero if:
  // 1. The user is logged in
  // 2. The show is represented in the continue watching data
  const shouldShowContinueWatchingHero = (
    isLoggedIn &&
    isShowInContinueWatchingData
  );

  let descriptionToDisplay;

  if (shouldShowContinueWatchingHero) {
    descriptionToDisplay = continueWatchingVideo.description_long || continueWatchingVideo.description_short || null;
  } else {
    descriptionToDisplay = show.description_long || show.description_short || null;
  }

  useEffect(() => {
    if(canAccessStorage('sessionStorage')) {
      setIsInternallyNavigating(sessionStorage.getItem(INTERNALLY_NAVIGATING_KEY) === 'true')
    }
  }, [])

  // @TODO at whatever point we add a user setting for auto play & sound, we will need to account for that here
  const hasAvailableFeaturedPreview = featuredPreview && featuredPreview.availability === 'available';
  const letsShowThePreview = hasAvailableFeaturedPreview && !hasPlayed && (isInternallyNavigating || showPreviewFromButton);
  const previewShouldBeMuted = !showPreviewFromButton;

  let showBackgroundImage: string | null = null;

  // For background art, prioritize the show's "background"
  if (show.images?.['background']) {
    showBackgroundImage = show.images['background'];
  } else if (videoToDirectTo?.image) {
    // fall back to the episode that we're sending users to's image
    // looking at you, NOVA
    showBackgroundImage = videoToDirectTo.image;
  }

  // we shift the preview layout controls up if there is a sponsorship row to render
  const shouldShiftControlsUp = show.funder_message != '' || (show.sponsor_logos && show.sponsor_logos?.length > 0);

  let moveControlsUpBreakpoint = undefined;

  if (shouldShiftControlsUp) {
    moveControlsUpBreakpoint = 1024;
  }

  const ShowPreviewOrBackgroundImage = () => {
    switch (true) {
      case shouldShowContinueWatchingHero: {
        const continueWatchingBackgroundImage =
          continueWatchingVideo?.images?.['asset-mezzanine-16x9'] ||
          continueWatchingVideo?.images?.['asset-kids-mezzanine1-16x9']

        if (continueWatchingBackgroundImage) {
          return (
            <ITSImage
              src={continueWatchingBackgroundImage}
              alt={show.title}
              width={860}
              height={480}
              loading="eager"
              fetchPriority="high"
            />
          )
        }
        // fall through if we don't have a background image for the continue watching video
        break
      }
      case letsShowThePreview: {
        const playerConfig = {
          autoplay: true,
          muted: previewShouldBeMuted,
          disableContinuousPlay: true,
          embedType: isSVP ? 'stationplayer' : 'portalplayer',
          host,
          previewLayout: true,
          shiftControlsUp: shouldShiftControlsUp,
          stationId: stationData?.id,
          callsign: stationData?.attributes.call_sign,
        }
        return (
          <VideoPlayer
            video={featuredPreview}
            playerConfig={playerConfig}
            moveControlsUpBreakpoint={moveControlsUpBreakpoint}
            onPlaybackEnded={() => setHasPlayed(true)}
          />
        )
      }
      case hasAvailableFeaturedPreview: {

        let className = styles.detail_hero_mezzanine_video_initializer;

        if (hasPlayed) {
          className += ` ${styles.detail_hero_mezzanine_video_initializer__has_played}`;
        }

        const handleMezzButtonClick = () => {
          setHasPlayed(false);
          setShowPreviewFromButton(true);
        };

        return (
          <MezzanineVideoInitializer
            videoType={featuredPreview.videoType.toLowerCase() as VideoTypeEnum}
            imgSrc={featuredPreview.mezzanine16x9ImageUrl}
            alt={featuredPreview.title}
            verticalOffsetButton={false}
            onClick={handleMezzButtonClick}
            width={860}
            className={className}
          />
        )
      }
      case (showBackgroundImage !== null): {
        const showBackgroundImageSizes = `
          (min-width: 1700px) 1118w,
          (min-width: 1024px) 65vw,
          100vw
        `
        return (
          <div className={styles.detail_hero_show_image}>
            <ITSImage
              src={showBackgroundImage}
              alt={show.title}
              width={860}
              height={480}
              srcSetSizes={[[320,180],[640,360],[860,480],[1118,629], [1677,943], [2236,1258]]}
              sizes={showBackgroundImageSizes}
              loading="eager"
              fetchPriority="high"
            />
          </div>
        )
      }

      default:
        return null
    }
  }

  let showPreviewBackgroundClassName = styles.detail_hero_background_image;

  if (letsShowThePreview) {
    showPreviewBackgroundClassName = styles.detail_hero_video
    if (isPreviewVideoPlayingWebKit) {
      showPreviewBackgroundClassName = `${styles.detail_hero_video} ${styles.detail_hero_webkit_mods}`;
    }
  }

  let logoToShow = null;

  if (show.images?.['white-logo-41']) {
    logoToShow = show.images['white-logo-41'];
  } else if (show.images?.['show-white-logo']) {
    logoToShow = show.images['show-white-logo'];
  }

  return (
    <div className={styles.detail_hero} data-sentry-component="ShowDetailHero" data-sentry-source-file="ShowDetailHero.tsx">
      <BackButton className={styles.detail_hero_back_button} data-sentry-element="BackButton" data-sentry-source-file="ShowDetailHero.tsx" />

      <div className={showPreviewBackgroundClassName}>
        <ShowPreviewOrBackgroundImage data-sentry-element="ShowPreviewOrBackgroundImage" data-sentry-source-file="ShowDetailHero.tsx" />
      </div>

      <div className={styles.detail_hero_overlay}>
        <ShowDetailNoAssetsMessage
          hasFullLengthAssets={Boolean(hasFullLengthAssets)}
          isLocalized={Boolean(stationData?.id)}
          show={show}
        data-sentry-element="ShowDetailNoAssetsMessage" data-sentry-source-file="ShowDetailHero.tsx" />

        <h1 className={styles.detail_hero_show_name}>
          { logoToShow ? (
            <ITSImage
              src={logoToShow}
              alt={show.title}
              width={300}
            />
          ) : (
            <>{show.title}</>
          )}
        </h1>

        { show.broadcast_info && (
          <p className={styles.detail_hero_broadcast_info}>{show.broadcast_info}</p>
        )}

        { shouldShowContinueWatchingHero && (
          <h2 className={styles.detail_hero_continue_watching_summary}>
            {continueWatchingVideoSummary}
          </h2>
        )}

        { descriptionToDisplay && (
          <ExpandableText
            text={descriptionToDisplay}
            className={styles.detail_hero_description}
          />
        )}

        { shouldShowContinueWatchingHero && (
          <div className={styles.detail_hero_progress_info}>
            <UserProgressBar
              slug={continueWatchingVideo.slug}
              className={styles.user_progress_bar}
              depPercentageWatched={depPercentageWatched}
              alwaysVisible={true}
            />
            <span>{`${minLeft} minute${minLeft === 1 ? "" : "s"} remaining`}</span>
          </div>
        )}

        { show.franchise && (
          <FromFranchise franchise={show.franchise} className={styles.show_franchise_label}/>
        )}

        <div className={styles.detail_hero_overlay_buttons}>
          { videoToDirectTo ? (
            <LinkButton
              style='blue'
              iconBefore="play"
              href={`/video/${shouldShowContinueWatchingHero ? continueWatchingVideo.slug : videoToDirectTo.slug}/`}
            >
              { shouldShowContinueWatchingHero ? "Resume Watching" : "Watch Now"}
            </LinkButton>
          ) : null }

          { show.website && (
            <LinkButton
              href={show.website}
              target="_blank"
              className={styles.detail_hero_overlay_buttons_website_button}
              >
              Visit Official Site
            </LinkButton>
          )}

          { !hasFullLengthAssets && show.genre && EXPLORE_TOPIC_CONFIGS[show.genre.slug] && (
            <LinkButton
              style='white_ghost'
              href={EXPLORE_TOPIC_CONFIGS[show.genre.slug].href}
            >
              {`Explore ${show.genre.title} Shows`}
            </LinkButton>
          )}

          <MyListButton show={show} style='iconOnly' data-sentry-element="MyListButton" data-sentry-source-file="ShowDetailHero.tsx" />

          { show.website && (
            <KabobMenu className={styles.detail_hero_overlay_buttons_kabob_menu}>
              <Link
                href={show.website}
                className={styles.detail_hero_overlay_buttons_kabob_menu_external_link_icon}>
                <ExternalLinkIcon
                  width="16"
                  height="13"
                />
                Visit The Official Site
              </Link>
            </KabobMenu>
          )}
        </div>
      </div>

      <SponsorshipVideoHelpRow
        source={show}
        title={show.title}
        sponsorInfoLink={sponsorInfoLink}
        className={styles.sponsorship_row__top}
        sponsorshipLogosPagePosition='top'
        sponsorshipTextVisibility='desktop' data-sentry-element="SponsorshipVideoHelpRow" data-sentry-source-file="ShowDetailHero.tsx" />
    </div>
  )
};

export default ShowDetailHero;
